import { Avatar, Card, Col, Divider, Input, Row, Space, Typography } from 'antd'
import React, { ChangeEvent, Dispatch } from 'react'
import { UserOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { s } from './style'
import { Documents } from './Documents'
import Details from './Details'
import Facilities from './Facilities'
import numeral from 'numeral'
import TourIcon from './TourIcon'
import { BestLayout } from './BestLayout'
import { GoogleMap, Marker, withGoogleMap } from 'react-google-maps'
import ReactMarkdown from 'react-markdown'
import { getAddress } from '../../../Graphql-Utilities'
import { ProposalInfoCardProposalFragment } from './ProposalInfoCard.generated-types'

const { TextArea } = Input
const { Text, Title, Paragraph } = Typography

interface MapProps {
  loadingElement: any
  containerElement: any
  mapElement: any
  lat: number
  lon: number
}

interface AvatarProps {
  avatar: any
}

const HeadBoxAvatar: React.FC<AvatarProps> = ({ avatar }) => {
  const url = avatar.small ? avatar.small : avatar.normal ? avatar.normal : avatar.thumb
  const SIZE = 64

  if (url) {
    return <Avatar size={SIZE} src={url} />
  }

  return <Avatar size={SIZE} icon={<UserOutlined />} />
}

const Map: React.ComponentClass<MapProps> = withGoogleMap(({ lat, lon }): JSX.Element => {
  const coords = {
    lat: lat,
    lng: lon,
  }

  return (
    <GoogleMap
      center={coords}
      defaultZoom={16}
      defaultOptions={{
        fullscreenControl: true,
        mapTypeControl: true,
        streetViewControl: false,
      }}>
      <Marker position={coords} />
    </GoogleMap>
  )
})

interface ProposalInfoCardProps {
  proposal: ProposalInfoCardProposalFragment
  editing: boolean
  setProposal: Dispatch<any>
}

export const ProposalInfoCard: React.FC<ProposalInfoCardProps> = ({
  proposal,
  editing,
  setProposal,
}) => {
  const { t } = useTranslation()
  const {
    space: {
      venue,
      description,
      spaceName,
      spatialConfigurations,
      hostContact,
      spaceFeatures,
      tour3dUrl,
    },
    documents,
    message,
  } = proposal

  const { nearestTransit, latitude, longitude } = venue

  const address = venue && getAddress(proposal)

  const facilities = []
  if (spaceFeatures?.length > 0) {
    spaceFeatures.forEach((f: any) => facilities.push(f.name))
  }

  const handleUpdateMessage = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    setProposal({ ...proposal, message: event.target.value })
  }

  return (
    <Card style={{ minWidth: 500 }}>
      <section style={s.header}>
        <section style={{ width: '100%' }}>
          <Row gutter={[8, 20]}>
            {hostContact?.avatar && (
              <Col span={2}>
                <HeadBoxAvatar avatar={hostContact.avatar} />
              </Col>
            )}
            <Col>
              <Row>
                <Title style={s.spaceName} level={2}>
                  {spaceName}
                </Title>
              </Row>
              <Row>
                <Text style={s.lightText}>{venue?.name}</Text>
              </Row>
            </Col>
          </Row>
        </section>
        {tour3dUrl && (
          <section style={s.tour}>
            <TourIcon width={24} height={24} />
            <a href={`${tour3dUrl}`} target='_blank' rel='noreferrer' style={{ margin: 4 }}>
              {t('proposal.virtualTour', 'Virtual tour')}
            </a>
          </section>
        )}
      </section>
      <Divider />
      <Row gutter={[8, 20]}>
        {nearestTransit && (
          <Col span={12}>
            <Space direction='vertical' size={'small'}>
              <Text style={s.lightText}>
                {t('proposal.closestTransportLink', 'Closest transportation link')}
              </Text>
              <Space direction='vertical' size={0}>
                <Text style={s.labelText} strong>
                  {nearestTransit.station}
                </Text>
                <Text style={s.midText}>
                  {`${numeral(nearestTransit.distanceKm).format('0.0')} ${t(
                    'proposal.distance',
                    'km away'
                  )}`}
                </Text>
              </Space>
            </Space>
          </Col>
        )}
        {spatialConfigurations?.length > 0 && (
          <Col span={12}>
            <Space direction='vertical' size={'small'}>
              <Text style={s.lightText}>{t('proposal.capacity', 'Capacity')}</Text>
              <BestLayout proposal={proposal} attendees={10} chosenLayout='boardroom' />
            </Space>
          </Col>
        )}
      </Row>
      {facilities?.length > 0 && (
        <section style={{ margin: '8px 0px' }}>
          <Text style={s.lightText}>{t('proposal.facilities', 'Facilities')}</Text>
          <br />
          <Facilities featureNames={facilities} />
        </section>
      )}
      <Divider />
      {message?.length > 0 && (
        <Row gutter={[8, 30]}>
          <Details title={t('proposal.messageFromVenue', 'Message from venue')}>
            {editing ? (
              <TextArea
                defaultValue={message}
                onChange={handleUpdateMessage}
                autoSize
                style={s.msgText}>
                {message}
              </TextArea>
            ) : (
              <Text style={s.msgText}>
                <ReactMarkdown>{message}</ReactMarkdown>
              </Text>
            )}
          </Details>
        </Row>
      )}
      {documents?.length > 0 && (
        <Row gutter={[8, 30]}>
          <Details title={t('proposal.attachments', 'Attachments')}>
            <Documents proposal={proposal} />
          </Details>
        </Row>
      )}
      <Row gutter={[8, 30]}>
        <Details title={t('proposal.space.desc', 'Space description')}>
          <Paragraph
            style={s.msgText}
            ellipsis={{ rows: 2, expandable: true, symbol: t('proposal.expandSpaceDesc', 'more') }}>
            <ReactMarkdown>{description}</ReactMarkdown>
          </Paragraph>
        </Details>
      </Row>
      {address && (
        <>
          <Row gutter={[8, 30]}>
            <Details title={t('proposal.space.loc', 'Location')}>
              <Text style={s.msgText}>{address}</Text>
            </Details>
          </Row>
          {latitude && longitude && (
            <Row gutter={[8, 30]}>
              <Map
                lat={latitude}
                lon={longitude}
                loadingElement={<div style={{ height: '100%' }} />}
                containerElement={<div style={{ height: 460, width: '100%' }} />}
                mapElement={<div style={{ height: '100%', width: '100%' }} />}
              />
            </Row>
          )}
        </>
      )}
    </Card>
  )
}
